import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, FileText, AlertCircle, Calendar as CalendarIcon } from 'lucide-react';
import api from '../../utils/api';
import moment from 'moment';

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(moment());
  const [view, setView] = useState('month'); // month, week, agenda
  const [events, setEvents] = useState([]);
  const [todos, setTodos] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const startOfMonth = currentDate.clone().startOf('month').startOf('week');
      const endOfMonth = currentDate.clone().endOf('month').endOf('week');

      // Fetch todos
      const todosResponse = await api.get('/todos', {
        params: {
          limit: 100,
        },
      });

      // Fetch cases with due dates
      const casesResponse = await api.get('/cases', {
        params: {
          limit: 100,
        },
      });

      const allTodos = todosResponse.data.data || [];
      const allCases = casesResponse.data.data || [];

      // Filter events within the month range
      const filteredTodos = allTodos.filter((todo) => {
        const dueDate = moment(todo.dueDate);
        return dueDate.isBetween(startOfMonth, endOfMonth, 'day', '[]');
      });

      const filteredCases = allCases.filter((caseItem) => {
        if (caseItem.dueDate) {
          const dueDate = moment(caseItem.dueDate);
          return dueDate.isBetween(startOfMonth, endOfMonth, 'day', '[]');
        }
        return false;
      });

      setTodos(filteredTodos);
      setCases(filteredCases);

      // Combine and format events
      const calendarEvents = [
        ...filteredTodos.map((todo) => ({
          id: todo._id,
          type: 'todo',
          title: todo.title,
          date: moment(todo.dueDate),
          description: todo.description,
          priority: todo.priority,
          status: todo.status,
          caseId: todo.caseId,
        })),
        ...filteredCases.map((caseItem) => ({
          id: caseItem._id,
          type: 'case',
          title: `Case #${caseItem.caseNumber}: ${caseItem.title}`,
          date: moment(caseItem.dueDate),
          description: caseItem.description,
          caseNumber: caseItem.caseNumber,
          priority: caseItem.priority,
        })),
      ];

      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate(currentDate.clone().subtract(1, 'month'));
  };

  const goToNextMonth = () => {
    setCurrentDate(currentDate.clone().add(1, 'month'));
  };

  const goToToday = () => {
    setCurrentDate(moment());
  };

  const getEventsForDate = (date) => {
    return events.filter((event) => event.date.isSame(date, 'day'));
  };

  const getEventIcon = (event) => {
    if (event.type === 'case') {
      return <FileText className="w-3 h-3" />;
    }
    if (event.priority === 'Urgent' || event.priority === 'High') {
      return <AlertCircle className="w-3 h-3 text-red-500" />;
    }
    return <FileText className="w-3 h-3" />;
  };

  const getEventColor = (event) => {
    if (event.type === 'case') {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    if (event.priority === 'Urgent') {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    if (event.priority === 'High') {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const renderMonthView = () => {
    const startOfMonth = currentDate.clone().startOf('month').startOf('week');
    const endOfMonth = currentDate.clone().endOf('month').endOf('week');
    const days = [];
    const day = startOfMonth.clone();

    while (day.isSameOrBefore(endOfMonth, 'day')) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <div className="grid grid-cols-7 border-b border-gray-200 min-w-[760px]">
          {weekDays.map((day) => (
            <div
              key={day}
              className="px-4 py-3 text-center text-sm font-medium text-gray-700 bg-gray-50"
            >
              {day}
            </div>
          ))}
        </div>
        <div className="divide-y divide-gray-200 min-w-[760px]">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 divide-x divide-gray-200">
              {week.map((date, dayIndex) => {
                const isCurrentMonth = date.isSame(currentDate, 'month');
                const isToday = date.isSame(moment(), 'day');
                const dayEvents = getEventsForDate(date);
                const isSelected = selectedDate && date.isSame(selectedDate, 'day');

                return (
                  <div
                    key={dayIndex}
                    className={`min-h-[120px] p-2 relative ${
                      !isCurrentMonth ? 'bg-gray-50' : 'bg-white'
                    } ${isToday ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-500 shadow-md' : ''} ${isSelected && !isToday ? 'ring-2 ring-blue-500' : ''} hover:bg-gray-50 cursor-pointer transition-all`}
                    onClick={() => setSelectedDate(date)}
                  >
                    {isToday && (
                      <div className="absolute top-1 right-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      </div>
                    )}
                    <div
                      className={`text-sm font-semibold mb-1 ${
                        !isCurrentMonth ? 'text-gray-400' : isToday ? 'text-blue-700 font-bold' : 'text-gray-900'
                      }`}
                    >
                      {date.format('D')}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((event) => (
                        <div
                          key={event.id}
                          className={`text-xs px-2 py-1 rounded border truncate ${getEventColor(
                            event
                          )}`}
                          title={event.title}
                        >
                          <div className="flex items-center space-x-1">
                            {getEventIcon(event)}
                            <span className="truncate">{event.title}</span>
                          </div>
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-500 px-2">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAgendaView = () => {
    const sortedEvents = [...events].sort((a, b) => a.date.diff(b.date));
    const groupedEvents = {};

    sortedEvents.forEach((event) => {
      const dateKey = event.date.format('YYYY-MM-DD');
      if (!groupedEvents[dateKey]) {
        groupedEvents[dateKey] = [];
      }
      groupedEvents[dateKey].push(event);
    });

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="divide-y divide-gray-200">
          {Object.keys(groupedEvents)
            .sort()
            .map((dateKey) => {
              const date = moment(dateKey);
              const dayEvents = groupedEvents[dateKey];

              const isToday = date.isSame(moment(), 'day');
              return (
                <div 
                  key={dateKey} 
                  className={`p-6 ${
                    isToday 
                      ? 'bg-gradient-to-r from-blue-50 to-transparent border-l-4 border-blue-500 shadow-sm' 
                      : ''
                  }`}
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className={`text-center relative ${isToday ? 'min-w-[80px]' : ''}`}>
                      {isToday && (
                        <div className="absolute -top-1 -right-1">
                          <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                        </div>
                      )}
                      <div className={`text-2xl font-bold ${
                        isToday ? 'text-blue-600' : 'text-gray-900'
                      }`}>
                        {date.format('D')}
                      </div>
                      <div className={`text-sm ${isToday ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>
                        {date.format('MMM')}
                      </div>
                      <div className={`text-xs ${isToday ? 'text-blue-500 font-medium' : 'text-gray-400'}`}>
                        {date.format('ddd')}
                      </div>
                    </div>
                    <div className="flex-1 space-y-3">
                      {dayEvents.map((event) => (
                        <div
                          key={event.id}
                          className={`p-3 rounded-lg border ${getEventColor(event)}`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-1">{getEventIcon(event)}</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm mb-1">{event.title}</div>
                              {event.description && (
                                <div className="text-xs text-gray-600 line-clamp-2">
                                  {event.description}
                                </div>
                              )}
                              <div className="flex items-center space-x-2 mt-2 text-xs text-gray-500">
                                <span>{event.date.format('h:mm A')}</span>
                                {event.priority && (
                                  <span className="px-2 py-0.5 rounded bg-white/50">
                                    {event.priority}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const startOfWeek = currentDate.clone().startOf('week');
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      weekDays.push(startOfWeek.clone().add(i, 'days'));
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <div className="grid grid-cols-7 border-b border-gray-200 min-w-[760px]">
          {weekDays.map((date) => {
            const isToday = date.isSame(moment(), 'day');
            return (
              <div
                key={date.format('YYYY-MM-DD')}
                className={`px-4 py-3 text-center border-r border-gray-200 last:border-r-0 relative ${
                  isToday 
                    ? 'bg-gradient-to-b from-blue-500 to-blue-600 text-white shadow-lg' 
                    : 'bg-gray-50'
                }`}
              >
                {isToday && (
                  <div className="absolute top-1 right-1">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  </div>
                )}
                <div className={`text-xs mb-1 ${isToday ? 'text-blue-100 font-medium' : 'text-gray-500'}`}>
                  {date.format('ddd')}
                </div>
                <div
                  className={`text-lg font-bold ${
                    isToday ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {date.format('D')}
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-7 divide-x divide-gray-200 min-h-[500px] min-w-[760px]">
          {weekDays.map((date) => {
            const isToday = date.isSame(moment(), 'day');
            const dayEvents = getEventsForDate(date);
            return (
              <div 
                key={date.format('YYYY-MM-DD')} 
                className={`p-2 min-h-[500px] relative ${
                  isToday ? 'bg-gradient-to-b from-blue-50/50 to-transparent border-l-4 border-blue-500' : ''
                }`}
              >
                <div className="space-y-1">
                  {dayEvents.map((event) => (
                    <div
                      key={event.id}
                      className={`text-xs px-2 py-1 rounded border ${getEventColor(event)}`}
                    >
                      <div className="flex items-center space-x-1">
                        {getEventIcon(event)}
                        <span className="truncate">{event.title}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {currentDate.format('MMMM YYYY')}
          </h1>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setView('month')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              view === 'month'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Month
          </button>
          <button
            onClick={() => setView('week')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              view === 'week'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setView('agenda')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              view === 'agenda'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Agenda
          </button>
          <button
            onClick={goToToday}
            className="px-4 py-2 rounded-lg font-medium text-sm bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 transition-colors"
          >
            Today
          </button>
        </div>
      </div>

      {/* Calendar View */}
      {view === 'month' && renderMonthView()}
      {view === 'week' && renderWeekView()}
      {view === 'agenda' && renderAgendaView()}

      {/* Selected Date Events Modal */}
      {selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedDate.format('MMMM D, YYYY')}
                </h2>
                <button
                  onClick={() => setSelectedDate(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {getEventsForDate(selectedDate).length === 0 ? (
                <p className="text-gray-500 text-center py-8">No events on this date</p>
              ) : (
                getEventsForDate(selectedDate).map((event) => (
                  <div
                    key={event.id}
                    className={`p-4 rounded-lg border ${getEventColor(event)}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">{getEventIcon(event)}</div>
                      <div className="flex-1">
                        <div className="font-medium mb-1">{event.title}</div>
                        {event.description && (
                          <div className="text-sm text-gray-600 mb-2">{event.description}</div>
                        )}
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <span>{event.date.format('h:mm A')}</span>
                          {event.priority && (
                            <span className="px-2 py-0.5 rounded bg-white/50">
                              {event.priority}
                            </span>
                          )}
                          {event.type === 'case' && (
                            <span className="px-2 py-0.5 rounded bg-white/50">Case</span>
                          )}
                          {event.type === 'todo' && (
                            <span className="px-2 py-0.5 rounded bg-white/50">To-Do</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
